import { inject, observer } from "mobx-react";
import React from "react";
import EstimatePaymentStep from "./estimate-payment-step";


const TERM_MONTHS_DEFAULT = '84'
const DOWNPAYMENT_PERCENTAGE_DEFAULT = 10

/**
 * Extracts APR bounds data from partner configuration
 * @param {Object} partner - Partner configuration object
 * @returns {Object} APR bounds data
*/
export function extractAprBoundsData(partner) {
   const defaultAprBounds = {
       "640_lower_apr": 18.99,
       "640_upper_apr": 23.99,
       "640_700_lower_apr": 14.74,
       "640_700_upper_apr": 18.99,
       "700_780_lower_apr": 12.74,
       "700_780_upper_apr": 14.74,
       "780_lower_apr": 10.49,
       "780_upper_apr": 12.74,
   };

   if (!partner?.partner_apr_bounds_by_rating) return defaultAprBounds;

   const extractedData = partner.partner_apr_bounds_by_rating.reduce((formattedValues, item) => {
      const rawRange = item.credit_range?.toString?.() ?? '';

      // Regex below is safe from ReDoS:
      // - No nested quantifiers
      // - Input is DB-driven (not user-controlled)
      // - Linear-time replacements (no backtracking traps)
      const rangeKey = rawRange
        .replace(/\D+/g, '_')    // Replace all non-digits with underscores
        .replace(/^_+/, '')      // Trim leading underscores
        .replace(/_+$/, '');     // Trim trailing underscores

      formattedValues[`${rangeKey}_lower_apr`] = item.lower_apr ?? defaultAprBounds[`${rangeKey}_lower_apr`];
      formattedValues[`${rangeKey}_upper_apr`] = item.upper_apr ?? defaultAprBounds[`${rangeKey}_upper_apr`];

      return formattedValues;
    }, {});

   return { ...defaultAprBounds, ...extractedData };
}

/**
 * Extracts preselected credit range from partner configuration
 * @param {Object} partner - Partner configuration object
 * @returns {Object} Credit range object with label and APR bounds
*/
export function extractPreselectedCreditRange(partner) {
   const defaultRange = {
       label: "780+",              // Default credit score label
       aprLowerBound: 10.49,       // Default minimum APR
       aprUpperBound: 12.74,       // Default maximum APR
   };

   // Return default range if partner has no APR bounds configuration
   if (!partner?.partner_apr_bounds_by_rating) return defaultRange;

   // Try to find a credit range that is marked as preselected
   const preselectedCreditRange = partner.partner_apr_bounds_by_rating.find(
       item => item.is_preselected
   );

   // If a preselected range is found, return its values
   if (preselectedCreditRange) {
       return {
           label: preselectedCreditRange.credit_range,      // Credit score range label
           aprLowerBound: preselectedCreditRange.lower_apr, // Minimum APR for this range
           aprUpperBound: preselectedCreditRange.upper_apr, // Maximum APR for this range
       };
   }

   // If no preselected range, try to find the highest credit range (780+)
   const creditRangeMax = partner.partner_apr_bounds_by_rating.find(
       item => item.credit_range === "780+"
   );

   // If highest range is found and has valid APR values, return those
   if (creditRangeMax.lower_apr != null && creditRangeMax.upper_apr != null) {
       return {
           label: creditRangeMax.credit_range,      // Highest credit score range label
           aprLowerBound: creditRangeMax.lower_apr, // Minimum APR for highest range
           aprUpperBound: creditRangeMax.upper_apr, // Maximum APR for highest range
       };
   }

   return defaultRange;
}

function getFeeDisclosure(docFee) {
   if (docFee == 0) {
      return "Monthly payment is calculated based upon a {term} month term, at {aprLowerBound} - {aprUpperBound}% APR, and ${amount} total amount financed. Excludes doc fee, tax, title, and license fees. Not all buyers or vehicles will qualify. Minimum $100 monthly payment is required. Terms are subject to change without notice.";
   } else {
      return "Monthly payment is calculated based upon a {term} month term, at {aprLowerBound} - {aprUpperBound}% APR, and ${amount} total amount financed. Includes ${docFee} doc fee and excludes tax, title, and license fees. Not all buyers or vehicles will qualify. Minimum $100 monthly payment is required. Terms are subject to change without notice.";
   }
}

export const EstimatePaymentStepContainer = inject(
   'store',
   'partner',
   'vehicleConfiguration',
   'rawVehicleData',
   'flowStore',
   'applicationUuid',
   'themeStore'
)(
    observer(
        class _EstimatePaymentStep extends React.Component {
            render() {
               const { store, partner, vehicleConfiguration, rawVehicleData, flowStore, applicationUuid, themeStore, ...props } = this.props;

               const termMonths = this.props.partner?.term_months || TERM_MONTHS_DEFAULT
               const aprBoundsData = extractAprBoundsData(this.props.partner)
               const vehiclePrice = Number(this.props.vehicleConfiguration?.msrp) || this.props.rawVehicleData?.raw_price || 0
               const downPaymentPercentage = this.props.partner?.cash_downpayment_percentage || DOWNPAYMENT_PERCENTAGE_DEFAULT
               const cashDownPayment = Math.round(vehiclePrice * (downPaymentPercentage / 100))
               const preselectedCreditRange = extractPreselectedCreditRange(this.props.partner)
               const docFee = this.props.partner?.doc_fee || 0
               const disclosure = getFeeDisclosure(docFee)
               const theme = this.props.themeStore || 'default'

               return (
                  <>
                     <EstimatePaymentStep
                        aprBounds={aprBoundsData}
                        termMonths={termMonths}
                        vehiclePrice={vehiclePrice}
                        preselectedCreditRange={preselectedCreditRange}
                        flowStore={flowStore}
                        applicationUuid={applicationUuid}
                        docFee={docFee}
                        disclosure={disclosure}
                        cashDownPayment={cashDownPayment}
                        themeStore={theme}
                        {...props}
                     />
                  </>
               );
            }
        }
    )
);
